import {Grid, Stack, Button, Menu, MenuItem, IconButton, Typography, Box} from "@mui/material";
import React, {useState} from "react";
import {IconCalendar, IconX, IconRefresh, IconChevronDown} from "@tabler/icons-react";
import moment from "moment";
import {ConversationsTable} from "./components/ConversationsTable";
import {ConversationPreviewModal} from "./components/ConversationPreviewModal";
import {ConversationComment} from "./components/ConversationComment";
import {ConversationStatusGauge} from "./components/ConversationStatusGauge";
import {DashboardContextProvider, useDashboard} from "../../../context/DashboardContext";
import {SpeechToTextCard} from "./components/SpeechToTextCard";
import {TextToSpeechCard} from "./components/TextToSpeechCard";
import {ChatbotCard} from "./components/ChatbotCard";
import {ActiveCallsCard} from "./components/ActiveCallsCard";
import {TagsUsageChart} from "./components/tags-usage-chart";


const DATE_RANGE_PRESETS = [
    {
        label: "Today",
        getValue: () => ({start: moment().startOf("day"), end: moment().endOf("day")})
    },
    {
        label: "Last 7 days",
        getValue: () => ({start: moment().subtract(6, "days").startOf("day"), end: moment().endOf("day")})
    },
    {
        label: "Last 30 days",
        getValue: () => ({start: moment().subtract(29, "days").startOf("day"), end: moment().endOf("day")})
    },
    {
        label: "This month",
        getValue: () => ({start: moment().startOf("month"), end: moment().endOf("day")})
    },
    {
        label: "Last month",
        getValue: () => ({
            start: moment().subtract(1, "month").startOf("month"),
            end: moment().subtract(1, "month").endOf("month")
        })
    },
];

const REFRESH_INTERVAL_PRESETS = [
    {label: "Off", value: null},
    {label: "10s", value: 10000},
    {label: "30s", value: 30000},
    {label: "1 min", value: 60000},
    {label: "5 min", value: 300000},
];

const chipButtonSx = {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.75,
    px: 1.5,
    py: 0.75,
    minWidth: 0,
    bgcolor: "var(--surface-2, #fff)",
    border: "1px solid",
    borderColor: "var(--border, #E5E4DD)",
    borderRadius: "20px",
    color: "text.primary",
    textTransform: "none",
    boxShadow: "none",
    fontSize: 13.5,
    fontWeight: 400,
    "&:hover": {
        bgcolor: "var(--surface-1, #FAFAF9)",
        boxShadow: "none",
        borderColor: "var(--border-strong, #C7C7C2)",
    },
};

const IntervalFilter = ({value, onChange}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const current = REFRESH_INTERVAL_PRESETS.find(p => p.value === value) || REFRESH_INTERVAL_PRESETS[0];

    return (
        <>
            <Button onClick={(e) => setAnchorEl(e.currentTarget)} sx={chipButtonSx}>
                <IconRefresh size={16} color="#8A8A85" strokeWidth={1.75}/>
                {current.label}
                <IconChevronDown size={14} color="#A3A39E" strokeWidth={1.75}/>
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                {REFRESH_INTERVAL_PRESETS.map((preset) => (
                    <MenuItem
                        key={preset.label}
                        onClick={() => {
                            onChange(preset.value);
                            setAnchorEl(null);
                        }}
                    >
                        {preset.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

const DateRangeFilter = ({value, onChange, onClear}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    return (
        <>
            <Button onClick={(e) => setAnchorEl(e.currentTarget)} sx={chipButtonSx}>
                <IconCalendar size={16} color="#8A8A85" strokeWidth={1.75}/>
                {value?.label || "Select period"}
                {value?.label ? (
                    <IconX
                        size={13}
                        style={{opacity: 0.5}}
                        onClick={(e) => {
                            e.stopPropagation();
                            onClear();
                        }}
                    />
                ) : (
                    <IconChevronDown size={14} color="#A3A39E" strokeWidth={1.75}/>
                )}
            </Button>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                {DATE_RANGE_PRESETS.map((preset) => (
                    <MenuItem
                        key={preset.label}
                        onClick={() => {
                            onChange({label: preset.label, ...preset.getValue()});
                            setAnchorEl(null);
                        }}
                    >
                        {preset.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export const ProjectDashboard = ({project}) => {
    return (
        <DashboardContextProvider project={project}>
            <ProjectDashboardContent project={project}/>
        </DashboardContextProvider>
    );
};

const ProjectDashboardContent = ({project}) => {
    const {
        statuses, tagUsage, dateRange, setDateRange,
        refreshInterval, setRefreshInterval, lastFetched
    } = useDashboard();

    const type = project?.settings?.projectType || "VOICE_BOT"

    return (
        <>
            <ConversationPreviewModal project={project}/>
            <ConversationComment project={project}/>
            <Grid container spacing={2}
                  sx={{p: {xs: 2, md: 4}, maxHeight: "calc(100dvh - 40px)", overflowY: "auto", overflowX: "hidden"}}>

                <Grid item xs={12}>
                    <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}
                           sx={{
                               px: 2,
                               py: 1.25,
                               mb: 0.5,
                               borderRadius: 2,
                           }}>
                        {lastFetched ? (
                            <Stack direction={"row"} alignItems={"center"} gap={0.75}>
                                <Box sx={{
                                    width: 6, height: 6, borderRadius: "50%",
                                    bgcolor: "#5BB98C",
                                }}/>
                                <Typography sx={{fontSize: 12.5, color: "text.secondary"}}>
                                    Updated at {moment(lastFetched).format("HH:mm:ss")}
                                </Typography>
                            </Stack>
                        ) : <Box/>}
                        <Stack direction={"row"} alignItems={"center"} gap={1.5}>
                            <IntervalFilter
                                value={refreshInterval}
                                onChange={setRefreshInterval}
                            />
                            <DateRangeFilter
                                value={dateRange}
                                onChange={setDateRange}
                                onClear={() => setDateRange(null)}
                            />
                        </Stack>
                    </Stack>
                </Grid>

                {
                    type === "VOICE_BOT" && <>
                        <Grid item xs={12} sm={6} lg={3}>
                            <SpeechToTextCard/>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <TextToSpeechCard/>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <ChatbotCard/>
                        </Grid>
                        <Grid item xs={12} sm={6} lg={3}>
                            <ActiveCallsCard/>
                        </Grid>
                    </>
                }

                <Grid item xs={12} sm={6} md={12} lg={7} xl={8}>
                    <ConversationsTable project={project}/>
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={5} xl={4}>
                    <Stack direction={"column"} gap={2} sx={{height: "100%"}}>
                        {/*<ConversationStatusPolar resolved={statuses?.RESOLVED || 0} unresolved={statuses?.UNRESOLVED || 0} missedCalls={statuses?.MISSED_CALL || 0} />*/}
                        <ConversationStatusGauge project={project} resolved={statuses?.RESOLVED || 0}
                                                 unresolved={statuses?.UNRESOLVED || 0}
                                                 missedCalls={statuses?.MISSED_CALL || 0} tagUsage={tagUsage}/>
                        <TagsUsageChart project={project}/>
                    </Stack>
                </Grid>

            </Grid>
        </>
    )
}